/**
 * Оповещения по цене.
 *
 * «Сообщи, когда BTC дойдёт до 80 000» — самое частое, чего человек
 * ждёт от терминала, и самое простое по смыслу. Живёт в контрактах, а
 * не в терминале: срабатывать оно обязано и при закрытой вкладке, то
 * есть решает сервер, а показывает — терминал. Правило пересечения одно
 * на двоих, иначе «на графике цена прошла, а оповещения не было»
 * объяснить было бы нечем.
 */
/** Цена прямой в момент `time` — прямая продолжается за свои точки. */
export function linePriceAt(line, time) {
    if (line.to.time === line.from.time)
        return line.from.price;
    return line.from.price + ((line.to.price - line.from.price) * (time - line.from.time)) / (line.to.time - line.from.time);
}
/** Уровень оповещения в момент `time`: у наклонной линии он свой в каждый момент. */
export function alertLevel(alert, time) {
    const line = alert.line;
    if (!line || line.to.time === line.from.time)
        return alert.price;
    return linePriceAt(line, time);
}
/** Границы канала в момент `time`: нижняя и верхняя. */
export function channelBounds(channel, time) {
    const a = linePriceAt(channel.first, time);
    const b = linePriceAt(channel.second, time);
    return { low: Math.min(a, b), high: Math.max(a, b) };
}
/** Переход через границы диапазона: вошла, вышла или коснулась границы. */
function rangeCrossed(condition, previous, current, low, high) {
    if (!Number.isFinite(previous) || !Number.isFinite(current))
        return false;
    const inside = (price) => price >= low && price <= high;
    const entered = !inside(previous) && inside(current);
    const left = inside(previous) && !inside(current);
    if (condition === 'exit')
        return left;
    if (condition === 'enter')
        return entered;
    // Касание границы: любое пересечение верхней или нижней, в том числе насквозь.
    return entered || left || priceCrossed('cross', previous, current, low) || priceCrossed('cross', previous, current, high);
}
/**
 * Сработало ли оповещение между двумя наблюдениями цены.
 *
 * Одна проверка на всё: и сервер, и терминал спрашивают её, поэтому
 * зона, линия и уровень срабатывают одинаково везде.
 */
export function alertTriggered(alert, previous, current, time) {
    if (alert.zone) {
        // Зона без условия выхода — как раньше: будит при входе.
        const condition = alert.condition === 'exit' ? 'exit' : 'enter';
        return rangeCrossed(condition, previous, current, alert.zone.low, alert.zone.high);
    }
    if (alert.channel) {
        const { low, high } = channelBounds(alert.channel, time);
        return rangeCrossed(alert.condition, previous, current, low, high);
    }
    const condition = alert.condition === 'enter' || alert.condition === 'exit' ? 'cross' : alert.condition;
    return priceCrossed(condition, previous, current, alertLevel(alert, time));
}
export const PRICE_ALERT_CONDITIONS = [
    { value: 'cross', label: 'Пересекает' },
    { value: 'up', label: 'Пересекает снизу вверх' },
    { value: 'down', label: 'Пересекает сверху вниз' },
];
/** Имя источника в журнале и в уведомлении. По-русски: его читает человек. */
export const PRICE_ALERT_NAME = 'Оповещение по цене';
/**
 * Пересекла ли цена уровень между двумя наблюдениями.
 *
 * Касание считается пересечением: цена, дошедшая ровно до 80 000 и
 * развернувшаяся, — это ровно то, о чём человек просил сообщить.
 */
export function priceCrossed(condition, previous, current, level) {
    if (!Number.isFinite(previous) || !Number.isFinite(current) || previous === current)
        return false;
    const up = previous < level && current >= level;
    const down = previous > level && current <= level;
    if (condition === 'up')
        return up;
    if (condition === 'down')
        return down;
    return up || down;
}
/** Условие словами: «BTCUSDT пересекает 80 768,0 снизу вверх». */
export function priceAlertText(alert, digits = 2, time = Date.now()) {
    const format = (value) => value.toLocaleString('ru-RU', {
        minimumFractionDigits: Math.min(digits, 8),
        maximumFractionDigits: Math.min(digits, 8),
    });
    const name = alert.symbol.replace('/', '');
    if (alert.zone) {
        const verb = alert.condition === 'exit' ? 'вышла из зоны' : 'вошла в зону';
        return `${name} ${verb} ${format(alert.zone.low)} – ${format(alert.zone.high)}`;
    }
    if (alert.channel) {
        const { low, high } = channelBounds(alert.channel, time);
        const verb = alert.condition === 'exit'
            ? 'вышла из канала'
            : alert.condition === 'enter'
                ? 'вошла в канал'
                : 'коснулась границы канала';
        return `${name} ${verb} — сейчас он ${format(low)} – ${format(high)}`;
    }
    if (alert.line) {
        const tail = alert.condition === 'up' ? ' снизу вверх' : alert.condition === 'down' ? ' сверху вниз' : '';
        return `${name} пересекает линию тренда${tail} — сейчас она на ${format(alertLevel(alert, time))}`;
    }
    const price = format(alert.price);
    const tail = alert.condition === 'up' ? ' снизу вверх' : alert.condition === 'down' ? ' сверху вниз' : '';
    return `${alert.symbol.replace('/', '')} пересекает ${price}${tail}`;
}
/**
 * Оповещения из профиля.
 *
 * Непонятная запись пропускается молча: оповещение, которое разбор не
 * понял, лучше не сработает, чем сработает на выдуманной цене.
 */
export function priceAlertsFrom(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    const seen = new Set();
    for (const entry of raw) {
        const alert = asPriceAlert(entry);
        if (!alert || seen.has(alert.id))
            continue;
        seen.add(alert.id);
        out.push(alert);
    }
    return out;
}
export function asPriceAlert(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const raw = value;
    if (typeof raw.id !== 'string' || raw.id === '')
        return null;
    if (typeof raw.venue !== 'string' || typeof raw.symbol !== 'string' || raw.symbol === '')
        return null;
    if (typeof raw.price !== 'number' || !Number.isFinite(raw.price) || raw.price <= 0)
        return null;
    const condition = raw.condition === 'up' || raw.condition === 'down' || raw.condition === 'enter' || raw.condition === 'exit'
        ? raw.condition
        : 'cross';
    const repeat = raw.repeat === 'every' ? 'every' : 'once';
    return {
        id: raw.id,
        venue: raw.venue,
        symbol: raw.symbol,
        price: raw.price,
        condition,
        repeat,
        ...(typeof raw.note === 'string' && raw.note.trim() !== '' ? { note: raw.note.trim().slice(0, 200) } : {}),
        active: raw.active !== false,
        createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : 0,
        ...zoneOf(raw.zone),
        ...lineOf(raw.line),
        ...channelOf(raw.channel),
        ...(typeof raw.drawingId === 'string' && raw.drawingId !== '' ? { drawingId: raw.drawingId } : {}),
    };
}
function finitePositive(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
function zoneOf(value) {
    if (typeof value !== 'object' || value === null)
        return {};
    const raw = value;
    if (!finitePositive(raw.low) || !finitePositive(raw.high))
        return {};
    return { zone: { low: Math.min(raw.low, raw.high), high: Math.max(raw.low, raw.high) } };
}
function channelOf(value) {
    if (typeof value !== 'object' || value === null)
        return {};
    const raw = value;
    const first = lineOf(raw.first);
    const second = lineOf(raw.second);
    return 'line' in first && 'line' in second ? { channel: { first: first.line, second: second.line } } : {};
}
function lineOf(value) {
    if (typeof value !== 'object' || value === null)
        return {};
    const raw = value;
    const point = (p) => {
        if (typeof p !== 'object' || p === null)
            return null;
        const q = p;
        return typeof q.time === 'number' && Number.isFinite(q.time) && finitePositive(q.price) ? { time: q.time, price: q.price } : null;
    };
    const from = point(raw.from);
    const to = point(raw.to);
    return from && to && from.time !== to.time ? { line: { from, to } } : {};
}
//# sourceMappingURL=alerts.js.map